import {Async, isLoaded} from 'Core/models/Async';
import {fromJS, Map as IMap, Map as ImmutableMap, List} from 'immutable';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {
  removeQuoteFormErrorEntries,
  updateBackdropState,
  updateOffer,
  updateQuoteFormErrors,
} from 'Quoting/ducks/createQuote';
import {A360ProductModel} from 'Quoting/models/Quoting';
import {createEisTypeObject, getByKeyPath, getKey, isDefined} from 'Quoting/utils/utilities';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {benefitOptions} from '../../MappingPaths';
import {createCoverageLinksObject} from '../../products/Common/censusClassUtils';
import * as paths from '../../products/GWL/PropertyPaths';
import {
  AccelDbRateFormatCd,
  AccelDeathBenefitType,
  GPLU_COVERAGE_CODE_TO_COVERAGE_TYPE_MAP,
  GPLUCoverageCode,
  GPLUCoverageDefinitionType,
  PwlRateFormatCd,
} from './gpluConstants';
import {
  createAmountObject,
  createBenefitPlanAccelExclusions,
  createBenefitPlanExclusions,
  createChildTLCoverageBenefitSchedule,
  createGPLUEligibilities,
  createSpouseAccelDeathCoverageBenefitSchedule,
  createSpouseUnderwritingAmounts,
  createSpouseWLCoverageBenefitSchedule,
  createSpouseWLCoverageBenefitSchedules,
  createWLCoverageBenefitSchedule,
  getDefaultCTLRMaxGIAmount,
} from './Plan1';

export const useCoverageToggle = () => {
  const dispatch = useDispatch();
  const {getQuoteFieldValue, updateQuoteFieldValues} = useQuoteField();
  const {selectedPlan, defaultPlanValues} = useSelector((state: ReducerState) => state.quoteForm);

  const planRootId = getQuoteFieldValue(`benefitPackagingDetail.plans[${selectedPlan?.planIndex}]._key.rootId`);

  const onToggleCoverageApply = async (
    coverageName: string,
    coverageAppliedPath: string,
    value: boolean,
    coverageDetailsPath: string,
    coverageIndex: number
  ) => {
    const coverageDefId = getQuoteFieldValue(
      `benefitPackagingDetail.plans[${selectedPlan?.planIndex}].coverageDefs[${coverageIndex}]._key.rootId`
    );
    const fieldsToUpdate: {path: string; newValue: any}[] = [
      {
        path: coverageAppliedPath,
        newValue: value,
      },
    ];

    if (value && !isDefined(getQuoteFieldValue(coverageDetailsPath))) {
      const coverageDetails = getCoverageDetailsObject(
        coverageName,
        defaultPlanValues?.get(coverageName),
        planRootId ?? '',
        coverageDefId ?? ''
      );

      fieldsToUpdate.push({
        path: coverageDetailsPath,
        newValue: fromJS(coverageDetails),
      });
    }
    updateQuoteFieldValues(fieldsToUpdate);
    dispatch(updateBackdropState(true));
    await new Promise<void>(resolve => {
      dispatch(updateOffer(resolve));
    });
    dispatch(updateBackdropState(false));
  };

  return {
    onToggleCoverageApply,
  };
};

export const getCoverageDetailsObject = (
  coverage: string,
  defaultValues: ImmutableMap<string, any> | undefined,
  planRootId: string,
  planParentId: string
) => {
  const key = getKey(planRootId, planParentId);
  const rootId = planRootId;
  const parentId = key.id;
  const coverageType = GPLU_COVERAGE_CODE_TO_COVERAGE_TYPE_MAP[coverage];
  const defaultCoverage = 'WLCoverage';
  switch (coverageType) {
    case GPLUCoverageDefinitionType.WL:
      defaultValues = defaultValues.get(GPLUCoverageCode.WHOLE_LIFE);
      return {
        _type: coverageType,
        _key: key,
        acceleratedBenefit: {
          isRequired: defaultValues.get('GPLUAcceleratedBenefitOptions.isRequired'),
          maxAcceleratedBenefitPct: defaultValues.get('GPLUAcceleratedBenefitOptions.maxAcceleratedBenefitPct'),
          maxAcceleratedBenefitAmount: createAmountObject(
            defaultValues.get('GPLUAcceleratedBenefitOptions.maxAcceleratedBenefitAmount')
          ),
          ...createEisTypeObject('GPLUAcceleratedBenefitOptions', rootId, parentId),
        },
        assumedParticipationPct: defaultValues.get(`${coverageType}.assumedParticipationPct`),
        benefitSchedule: createWLCoverageBenefitSchedule(defaultValues, rootId, parentId),
        code: coverage,
        coverageGroupsLinks: [],
        censusTypeCd: defaultValues.get(`${coverageType}.censusTypeCd`),
        eligibilities: createGPLUEligibilities(defaultValues, rootId, parentId, ''),
        exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId),
        fundingStructure: {
          ...createEisTypeObject('GPLUBenefitCoverageFundingStructure', rootId, parentId),
          taxabilityCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.taxabilityCd'),
          contributionTypeCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.contributionTypeCd'),
          contributionBasisCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.contributionBasisCd'),
          sponsorContributionAmount: createAmountObject(
            defaultValues.get('GPLUBenefitCoverageFundingStructure.sponsorContributionAmount')
          ),
          participantContributionPct: defaultValues.get(
            'GPLUBenefitCoverageFundingStructure.participantContributionPct'
          ),
          minParticipantsNum: defaultValues.get('GPLUBenefitCoverageFundingStructure.minParticipantsNum'),
          payrollFrequencyCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.payrollFrequencyCd'),
        },
        hasApl: false,
        isPortabilityApplied: defaultValues.get(`${coverageType}.isPortabilityApplied`),
        nonForfeitureDefaultOptionCd: defaultValues.get(`${coverageType}.nonForfeitureDefaultOptionCd`),
        ratingDetails: {
          rateFormatCd: defaultValues.get('GPLUBenefitCoverageRatingDetails.rateFormatCd'),
          rateBasisCd: defaultValues.get('GPLUBenefitCoverageRatingDetails.rateBasisCd'),
          ...createEisTypeObject('GPLUBenefitCoverageRatingDetails', rootId, parentId),
        },
        underwriting: {
          giStatus: defaultValues.get('GPLUUnderwriting.giStatus'),
          giTimePeriod: defaultValues.get('GPLUUnderwriting.giTimePeriod'),
          guaranteedIssueAmount: createAmountObject(defaultValues.get('GPLUUnderwriting.guaranteedIssueAmount')),
          isEOIForLateEntrant: defaultValues.get('GPLUUnderwriting.isEOIForLateEntrant'),
          isEOIRequired: defaultValues.get('GPLUUnderwriting.isEOIRequired'),
          underwritingTypeCd: defaultValues.get('GPLUUnderwriting.underwritingTypeCd'),
          maxUnderWritingLimitAllowance: createAmountObject(
            defaultValues.get('GPLUUnderwriting.maxUnderWritingLimitAllowance')
          ),
          maxDeathBenefitWithPrimaryInsuredTermLife: createAmountObject(
            defaultValues.get('GPLUUnderwriting.maxDeathBenefitWithPrimaryInsuredTermLife')
          ),
          ...createEisTypeObject('GPLUUnderwriting', rootId, parentId),
        },
      };
    case GPLUCoverageDefinitionType.SPOUSE_WL: {
      const pwlDefaultValues = defaultValues.get(GPLUCoverageCode.WHOLE_LIFE);
      defaultValues = defaultValues.get(GPLUCoverageCode.SPOUSE_WHOLE_LIFE);
      return {
        _type: coverageType,
        _key: key,
        acceleratedBenefit: {
          isRequired: pwlDefaultValues?.get('GPLUAcceleratedBenefitOptions.isRequired'),
          maxAcceleratedBenefitPct: pwlDefaultValues?.get('GPLUAcceleratedBenefitOptions.maxAcceleratedBenefitPct'),
          maxAcceleratedBenefitAmount: createAmountObject(
            pwlDefaultValues?.get('GPLUAcceleratedBenefitOptions.maxAcceleratedBenefitAmount')
          ),
          ...createEisTypeObject('GPLUAcceleratedBenefitOptions', rootId, parentId),
        },
        benefitSchedule: createSpouseWLCoverageBenefitSchedule(defaultValues, rootId, parentId),
        benefitSchedules: ['Ws'].map(type =>
          createSpouseWLCoverageBenefitSchedules(defaultValues, rootId, parentId, type)
        ),
        code: coverage,
        coverageGroupsLinks: [],
        eligibilities: createGPLUEligibilities(defaultValues, rootId, parentId, 'Age'),
        exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId),
        fundingStructure: {
          ...createEisTypeObject('GPLUBenefitReducedFundingStructure', rootId, parentId),
          sponsorContributionAmount: createAmountObject(
            defaultValues.get('GPLUBenefitCoverageFundingStructure.sponsorContributionAmount')
          ),
          taxabilityCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.taxabilityCd'),
          contributionTypeCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.contributionTypeCd'),
          contributionBasisCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.contributionBasisCd'),
          participantContributionPct: defaultValues.get(
            'GPLUBenefitCoverageFundingStructure.participantContributionPct'
          ),
        },
        ratingDetails: {
          rateBasisCd: pwlDefaultValues?.get('GPLUBenefitCoverageRatingDetails.rateBasisCd'),
          ...createEisTypeObject('GPLUBenefitCoverageRatingDetails', rootId, parentId),
        },
        nonForfeitureDefaultOptionCd: defaultValues.get(`${coverageType}.nonForfeitureDefaultOptionCd`),
        isPortabilityApplied: defaultValues.get(`${defaultCoverage}.isPortabilityApplied`),
        censusTypeCd: defaultValues.get(`${coverageType}.censusTypeCd`),
        underwriting: {
          offerStatus: 'offered',
          giStatus: defaultValues.get('GPLUUnderwriting.giStatus'),
          isEOIRequired: defaultValues.get('GPLUUnderwriting.isEOIRequired'),
          isEOIForLateEntrant: defaultValues.get('GPLUUnderwriting.isEOIForLateEntrant'),
          giTimePeriod: defaultValues.get('GPLUUnderwriting.giTimePeriod'),
          underwritingTypeCd: defaultValues.get('GPLUUnderwriting.underwritingTypeCd'),
          spouseUnderwritingAmounts: ['GPLUSpouseUnderwritingAmountsWs'].map(amountType =>
            createSpouseUnderwritingAmounts(defaultValues, amountType, rootId, parentId)
          ),
          ...createEisTypeObject('AbGPLUUnderwriting', rootId, parentId),
        },
        offerStatus: 'offered',
      };
    }
    case GPLUCoverageDefinitionType.ChildTLCoverage:
      defaultValues = defaultValues.get(GPLUCoverageCode.CHILD_TERM_LIFE);
      return {
        _type: coverageType,
        _key: key,
        benefitSchedule: createChildTLCoverageBenefitSchedule(defaultValues, rootId, parentId),
        code: coverage,
        coverageGroupsLinks: [],
        eligibilities: createGPLUEligibilities(defaultValues, rootId, parentId, 'Age'),
        exclusions: createBenefitPlanExclusions(defaultValues, rootId, parentId),
        fundingStructure: {
          ...createEisTypeObject('GPLUBenefitReducedFundingStructure', rootId, parentId),
          sponsorContributionAmount: createAmountObject(
            defaultValues.get('GPLUBenefitCoverageFundingStructure.sponsorContributionAmount')
          ),
          taxabilityCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.taxabilityCd'),
          contributionBasisCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.contributionBasisCd'),
          contributionTypeCd: defaultValues.get('GPLUBenefitCoverageFundingStructure.contributionTypeCd'),
          participantContributionPct: defaultValues.get(
            'GPLUBenefitCoverageFundingStructure.participantContributionPct'
          ),
        },
        ratingDetails: {
          rateBasisCd: defaultValues.get('GPLUBenefitCoverageRatingDetails.rateBasisCd'),
          rateFormatCd: defaultValues.get('GPLUBenefitCoverageRatingDetails.rateFormatCd'),
          ...createEisTypeObject('GPLUBenefitCoverageRatingDetails', rootId, parentId),
        },
        underwriting: {
          isEOIRequired: defaultValues.get('GPLUUnderwriting.isEOIRequired'),
          underwritingTypeCd: defaultValues.get('GPLUUnderwriting.underwritingTypeCd'),
          guaranteedIssueAmount: createAmountObject(getDefaultCTLRMaxGIAmount(defaultValues) as any),
          ...createEisTypeObject('GPLUUnderwriting', rootId, parentId),
        },
        terminationAge: defaultValues.get(`${coverageType}.terminationAge`),
        childTerminationAge: defaultValues.get(`${coverageType}.childTerminationAge`),
        isRequiredChildTLCoverage: defaultValues.get(`${coverageType}.isRequiredChildTLCoverage`),
      };
    case GPLUCoverageDefinitionType.AccelDeathBenefitCoverage:
      defaultValues = defaultValues.get(GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE);
      return {
        _type: coverageType,
        _key: key,
        benefitScheduleLTCChronic: createSpouseAccelDeathCoverageBenefitSchedule(defaultValues, rootId, parentId),
        eligibilities: createGPLUEligibilities(defaultValues, rootId, parentId, 'Age'),
        eliminationPeriod: defaultValues.get(`${coverageType}.eliminationPeriod`),
        code: coverage,
        coverageGroupsLinks: [],
        exclusions: createBenefitPlanAccelExclusions(defaultValues, rootId, parentId),
        preExLimitation: {
          preExConditionLimitationCd: defaultValues.get('GPLUPreExLimitation.preExConditionLimitationCd'),
          preExConditionLimitationPeriodCd:
            defaultValues.get('GPLUPreExLimitation.preExConditionLimitationCd') === 'PreEx'
              ? defaultValues.get('GPLUPreExLimitation.preExConditionLimitationPeriodCd')
              : undefined,
          lookBackPeriodCd:
            defaultValues.get('GPLUPreExLimitation.preExConditionLimitationCd') === 'PreEx'
              ? defaultValues.get('GPLUPreExLimitation.lookBackPeriodCd')
              : undefined,
          ...createEisTypeObject('GPLUPreExLimitation', rootId, parentId),
        },
        ratingDetails: {
          rateBasisCd: defaultValues.get('GPLUBenefitCoverageRatingDetails.rateBasisCd'),
          rateFormatCd: defaultValues.get('GPLUBenefitCoverageRatingDetails.rateFormatCd'),
          ...createEisTypeObject('GPLUBenefitCoverageRatingDetails', rootId, parentId),
        },
        isRequiredAccelDeathBenefitCoverage:
          defaultValues.get(`${coverageType}.isRequiredAccelDeathBenefitCoverage`) ?? true,
        offerStatus: 'offered',
      };
    default:
      return null;
  }
};

export const updateCoveragesfromPWL = (planIndex: number, quote: Async<ImmutableMap<string, any>>, covObject: any) => {
  const quoteData = isLoaded(quote) ? quote.Data : null;
  const quoteModel = quoteData.get('_modelName');
  const selectedPlanRootId = getByKeyPath(
    quoteData,
    `benefitPackagingDetail.plans[${planIndex}]._key.rootId`
  ) as string;
  const planCoverageDefs = getByKeyPath(
    quoteData,
    `benefitPackagingDetail.plans[${planIndex ?? 0}].coverageDefs`
  ) as List<IMap<string, any>>;

  const pwlCoverageIndex = planCoverageDefs?.findIndex((cov: any) => {
    return cov.get('_type') === GPLUCoverageDefinitionType.WL;
  });

  const pwlOptionsPaths = paths.getGPLUOptionsBlockPropertyPaths(planIndex, pwlCoverageIndex);
  const pwlRatingPaths = paths.getGPLURatingBlockPropertyPaths(planIndex, pwlCoverageIndex);
  const pwlCoverageLinks: List<any> = getByKeyPath(
    quoteData,
    `${paths.getParentPath(planIndex, pwlCoverageIndex)}.coverageGroupsLinks`
  ) as List<any>;
  const coverageLinks = pwlCoverageLinks.map(covLink =>
    createCoverageLinksObject(
      quoteModel as A360ProductModel,
      selectedPlanRootId,
      covObject._key.id,
      getByKeyPath(covLink, 'groupLink._ref') as string
    )
  );
  covObject.coverageGroupsLinks = coverageLinks;
  switch (covObject.code) {
    case GPLUCoverageCode.SPOUSE_WHOLE_LIFE:
      covObject.ratingDetails.rateFormatCd = getByKeyPath(quoteData, pwlRatingPaths.rateFormatCd);
      covObject.acceleratedBenefit.maxAcceleratedBenefitPct = getByKeyPath(
        quoteData,
        pwlOptionsPaths.maxAcceleratedBenefitPct
      );
      covObject.acceleratedBenefit.maxAcceleratedBenefitAmount = getByKeyPath(
        quoteData,
        pwlOptionsPaths.maxAcceleratedBenefitAmount
      );
      return covObject;
    case GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE: {
      let accelDbRateFormatCd: string;
      const pwlRateFormat = getByKeyPath(quoteData, pwlRatingPaths.rateFormatCd);
      if (pwlRateFormat === PwlRateFormatCd['AgeBanded-TobaccoDistinct']) {
        accelDbRateFormatCd = AccelDbRateFormatCd['AgeBanded-Uni-Tobacco'];
      }
      if (pwlRateFormat === PwlRateFormatCd['AgeSpecific-TobaccoDistinct']) {
        accelDbRateFormatCd = AccelDbRateFormatCd['AgeSpecific-Uni-Tobacco'];
      }
      covObject.ratingDetails.rateFormatCd = accelDbRateFormatCd;
      return covObject;
    }
    default:
      return covObject;
  }
};

export const showInflationProtectionOrOptions = (
  accelDeathBenefitType: AccelDeathBenefitType,
  situsStateCd: string
) => {
  const allowedStates = ['UT', 'CA', 'ME', 'OH', 'PA', 'TN', 'TX'];
  return (
    (accelDeathBenefitType === AccelDeathBenefitType.LTC_Basic && situsStateCd === 'UT') ||
    (accelDeathBenefitType === AccelDeathBenefitType.LTC_Restoration_Extension && allowedStates.includes(situsStateCd))
  );
};

export const getBenefitLabelMappingforGPLU = (benefitName: string, parentPath: string) => {
  return Object.entries(benefitOptions[benefitName]).map(([k, v], newIndex) => {
    const exclusionPath = `${parentPath}.${k}`;
    return {label: v, path: exclusionPath};
  });
};

export const useModifyMultipleValuesQuoteFormErrors = () => {
  const dispatch = useDispatch();

  const modifyMVQuoteFormErrors = (
    val: number[],
    guaranteedIssueAmount: number,
    formFieldId: string,
    coverage: string
  ) => {
    if (!(val.length > 0)) {
      dispatch(
        updateQuoteFormErrors({
          [formFieldId]: `'Specified Benefit Amounts' is mandatory under ${coverage}`,
        })
      );
    } else if (val.length > 0 && !val.some(v => v > guaranteedIssueAmount)) {
      dispatch(removeQuoteFormErrorEntries([formFieldId]));
    }
  };

  return {
    modifyMVQuoteFormErrors,
  };
};
